// A collection's list as its address holds it: ?q=&sort=&dir=&group=&page=&type=
// and the filters (f.<key>=, min.<key>=, max.<key>=, place=, done=, rated=,
// avg=; phase 3 §3.4) — read by the page, its item pages and saved views.
import { LOCATION_KEY, fieldKey } from '../../fieldKeys';
import { filtersFromParams, writeFilters } from './filters';

// The Location column (items.location_id, sorted by its path) has the
// reserved key `location`, so no field can collide with it.
export const LOCATION_SORT_KEY = LOCATION_KEY;
// How the Location column is written in ?sort= and sent to the API.
export const LOCATION_SORT_PARAM = '@location';

// A ?sort= saved before field keys names the field: map it to the field's key.
const sortKeyFor = (key, inventory) => {
  if (key === LOCATION_SORT_PARAM || key === LOCATION_SORT_KEY) return LOCATION_SORT_KEY;
  if (['title', 'year', 'created_at', 'updated_at'].includes(key)) return key;
  const fields = [...(inventory?.fields || []), ...(inventory?.item_types || []).flatMap(type => type.fields || [])];
  if (fields.some(field => fieldKey(field) === key)) return key;
  const named = fields.find(field => field.name === key)
    || fields.find(field => (field.name || '').toLowerCase() === key.toLowerCase());
  return named ? fieldKey(named) : key;
};
const positiveInt = (value) => {
  const n = parseInt(value, 10);
  return Number.isInteger(n) && n > 0 && String(n) === String(value).trim() ? n : null;
};

export const listFromParams = (params, inventory) => {
  const key = params.get('sort');
  const group = (params.get('group') || '').trim();
  return {
    q: (params.get('q') || '').trim(),
    sortKey: key ? sortKeyFor(key, inventory) : null,
    direction: key ? (params.get('dir') === 'desc' ? 'desc' : 'asc') : null,
    group: /^(@[a-z]+|[a-z][a-z0-9_]{0,63})$/.test(group) ? group : null,
    page: positiveInt(params.get('page')) || 1,
    type: positiveInt(params.get('type')),
    filters: filtersFromParams(params),
  };
};

export const paramsFromList = (list) => {
  const next = new URLSearchParams();
  if (list.q) next.set('q', list.q);
  if (list.sortKey) {
    next.set('sort', list.sortKey === LOCATION_SORT_KEY ? LOCATION_SORT_PARAM : list.sortKey);
    next.set('dir', list.direction || 'asc');
  }
  if (list.group) next.set('group', list.group);
  if (list.page > 1) next.set('page', String(list.page));
  if (list.type) next.set('type', String(list.type));
  writeFilters(next, list.filters);
  return next;
};

/**
 * A list's query string in one form, without its page: two addresses of the
 * same list (parameters in another order, a page, an old sort by name) give
 * the same text — how a saved view is recognised in the address bar.
 */
export const canonicalQuery = (search, inventory) => {
  const params = typeof search === 'string' ? new URLSearchParams(search.replace(/^\?/, '')) : search;
  return paramsFromList({ ...listFromParams(params, inventory), page: 1 }).toString();
};
